/* eslint-disable @typescript-eslint/no-explicit-any */
import { userLogin } from "@/services/user";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { useAnalytics } from "@/analytics/AnalyticsContext";
import { VISITOR_EVENTS } from "@/analytics/events";
import { addUserInformation, LoginResponseData } from "@/store/userSlice";
import { isBoardingProcessingStatue } from "@/types/employeeState";
import {
  analyzeDemographics,
  getAnonymizedDemographics,
} from "@/utils/demographicAnalysis";
import { identifyVisitor } from "@/utils/visitorIdentification";
import PublicLayout from "../Common/Containers/PublicLayout";
import { LoginForm, LoginFormInput } from "./LoginForm";

// Kept for backward compatibility — test utils and other components import this type.
// Represents PascalCase JWT claim fields as they arrive on the wire.
export type UserData = {
  readonly UserName: string;
  readonly UserAccountID: string;
  readonly FirstName: string;
  readonly MiddleName: string;
  readonly LastName: string;
  readonly EmailAddress: string;
  readonly EmployeeID: string;
  readonly EmployerID: string;
  readonly SootchyEmployerID: number | string;
  readonly VestingScheduleName: string;
  readonly IsDefaultPasswordChanged: "True" | "False" | string;
  readonly EnrolledStatus: string;
  readonly OnboardingProgress: string;
  readonly DateOfBirth: string;
  readonly EmployeeSalary: string | number;
  readonly is2FAEnabled: string;
  readonly EmployerMaxContribution?: string | number;
  readonly BenefitContributionLimit?: string | number;
  readonly BenefitPlanStartDate?: string;
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackEvent, setUser } = useAnalytics();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const search = useLocation().search;
  const searchParams = new URLSearchParams(search).toString();
  /*   const [showNewPasswordScreen, setShowNewPasswordScreen] =
    useState<boolean>(false);
  const user = useSelector((state: RootState) => state.user); */
  const [error, setError] = useState<string | null>(null);

  const params = new URLSearchParams(location.search);
  const employeeData = params.get("employee");

  useEffect(() => {
    if (employeeData) {
      const decodedData = atob(employeeData);
      const jsonData = JSON.parse(decodedData);

      setUsername(jsonData.username?.trim() || "");
      setPassword(jsonData.password?.trim() || "");
    } else {
      setUsername("");
      setPassword("");
    }
  }, [employeeData]);

  const handleLogin = async (loginData: LoginFormInput) => {
    setIsLoading(true);

    try {
      const trimmedUsername = loginData.username.trim();
      const trimmedPassword = loginData.password.trim();

      if (!password || !username) {
        setPassword(trimmedPassword);
        setUsername(trimmedUsername);
      }

      // Mint a fresh reCAPTCHA v3 token at submit time (single-use, ~2 min
      // TTL). Degrade gracefully if the script is blocked or not yet ready so
      // users aren't locked out.
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha("login");
        } catch {
          // intentionally empty — proceed without a token
        }
      }

      const { data }: { data: LoginResponseData } = await userLogin({
        username: trimmedUsername,
        password: trimmedPassword,
        recaptchaToken,
      });

      if (data?.userAccountID) {
        // Persist session data (PascalCase) for getDecodedTokenSafe callers.
        localStorage.setItem(
          "sootchy_user_session",
          JSON.stringify({
            UserAccountID: data.userAccountID,
            UserName: data.userName,
            FirstName: data.firstName,
            MiddleName: data.middleName,
            LastName: data.lastName,
            EmailAddress: data.emailAddress,
            EmployeeID: data.employeeID,
            EmployerID: data.employerID,
            SootchyEmployerID: data.sootchyEmployerID,
            VestingScheduleName: data.vestingScheduleName,
            OnboardingProgress: data.onboardingProgress,
            IsDefaultPasswordChanged: data.isDefaultPasswordChanged,
            Is2FAEnabled: data.is2FAEnabled,
            EnrolledStatus: data.enrolledStatus,
            DateOfBirth: data.dateOfBirth,
            EmployeeSalary: data.employeeSalary,
            EmployerMaxContribution: data.employerMaxContribution,
            BenefitContributionLimit: data.benefitContributionLimit,
            BenefitPlanStartDate: data.benefitPlanStartDate,
          }),
        );

        dispatch(addUserInformation(data));

        try {
          const userForAnalytics = {
            id: data.userAccountID,
            username: data.userName,
            onboardingProgress: data.onboardingProgress,
            IsDefaultPasswordChanged: String(data.isDefaultPasswordChanged),
          };

          const visitorResult = identifyVisitor(userForAnalytics);

          setUser(data.userAccountID);

          const eventData = {
            user_id: data.userAccountID,
            visitor_type: visitorResult.visitorType,
            login_count: visitorResult.session.loginCount,
          };

          if (visitorResult.isFirstLogin) {
            trackEvent(VISITOR_EVENTS.USER_FIRST_AUTHENTICATION, eventData);
          } else {
            trackEvent(VISITOR_EVENTS.USER_RETURNING_AUTHENTICATION, eventData);
          }

          try {
            const demographics = analyzeDemographics({
              DateOfBirth: data.dateOfBirth,
              FirstName: data.firstName,
              EmployeeSalary: String(data.employeeSalary),
            });

            const anonymizedDemographics =
              getAnonymizedDemographics(demographics);

            trackEvent(VISITOR_EVENTS.USER_DEMOGRAPHICS_DETECTED, {
              user_id: data.userAccountID,
              ...anonymizedDemographics,
            });
          } catch {
            // Handle demographic analysis errors silently to avoid impacting user experience
          }
        } catch {
          // Handle analytics errors silently to avoid impacting user experience
        }

        if (isBoardingProcessingStatue(data.onboardingProgress)) {
          navigate(`/?${searchParams}`, { replace: true });
        } else {
          navigate("/wizard", { replace: true });
        }
      }
    } catch (error: any) {
      if (error.status === 403) {
        setError(t("auth.login.errors.invalid_credentials"));
      } else {
        setError(t("auth.login.errors.general_error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  {
    /**Now handled by the Create Password Page, will delete laer */
  }
  /* const handleCreatePassword = async (data: createPasswordInput) => {
    setIsLoading(true);

    try {
      const trimmedPassword = data.password.trim();

      await updatePassword({
        userAccountID: parseInt(user.id),
        currentPassword: password.trim(),
        newPassword: trimmedPassword,
      });
      //dispatch(updateDefaultPasswordStatus("True"));
      navigate("/wizard");
    } catch (err) {
      setError(
        "Invalid credentials, please try again with your correct password"
      );
    } finally {
      setIsLoading(false);
    }
  }; */

  return (
    <PublicLayout>
      <div className="flex-grow flex items-center justify-center px-4  h-full font-outfit">
        <div className="w-full max-w-4xl flex justify-center">
          {/* Left side - Education program */}
          <div className="hidden order-last md:order-first w-full md:w-1/2 pr-8 md:flex flex-col justify-center">
            <h2 className="text-xl md:text-2xl mb-4">
              {t("auth.sootchy_program.title", {
                partnerName: window.partner?.title ?? "Sootchy",
              })}
            </h2>
            <ul className="space-y-2">
              <li className="flex items-center py-2">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t("auth.sootchy_program.benefits.auto_setup")}
              </li>
              <li className="flex items-center py-2">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t("auth.sootchy_program.benefits.employer_match")}
              </li>
              <li className="flex items-center py-2">
                <svg
                  className="w-5 h-5 text-green-500 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t("auth.sootchy_program.benefits.tax_free_growth")}
              </li>
            </ul>
          </div>

          {/* Right side - Login form */}
          <div className="font-outfit w-[420px] mt-[40px]">
            {/* {showNewPasswordScreen ? (
              <CreateNewPasswordForm
                loading={isLoading}
                error={error}
                onSubmit={handleCreatePassword}
                resetError={() => setError("")}
              />
            ) : ( */}
            <LoginForm
              error={error}
              onSubmit={handleLogin}
              loading={isLoading}
              username={username}
              password={password}
              resetError={() => setError("")}
            />
            {/* )} */}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Login;
